import type { GradientFill, GradientStop, OuterShadow, TextTransform } from './types'

export const createDefaultGradient = (idPrefix = 'gradient-stop'): GradientFill => ({
  type: 'linear',
  angle: 0,
  centerX: 0.5,
  centerY: 0.5,
  radius: 0.5,
  stops: [
    { id: `${idPrefix}-start`, color: '#E66B3C', position: 0 },
    { id: `${idPrefix}-end`, color: '#F2C94C', position: 1 },
  ],
})

export const createDefaultShadow = (): OuterShadow => ({
  enabled: false,
  color: '#171A17',
  opacity: 0.28,
  offsetX: 1,
  offsetY: 1,
  blur: 1.5,
})

const finite = (value: unknown, fallback: number) => {
  const number = Number(value)
  return Number.isFinite(number) ? number : fallback
}

export const clamp01 = (value: unknown) => Math.min(1, Math.max(0, finite(value, 0)))

export const normalizedGradientStops = (stops: GradientStop[]) => {
  const normalized = (Array.isArray(stops) ? stops : [])
    .map((stop, index) => ({
      id: String(stop?.id || `gradient-stop-${index}`),
      color: String(stop?.color || '#000000'),
      position: clamp01(stop?.position),
    }))
    .sort((a, b) => a.position - b.position)
  if (normalized.length >= 2) return normalized
  return createDefaultGradient().stops
}

export const gradientStopBindingKey = (stopId: string, property: 'color' | 'position') =>
  `gradient.stops.${stopId}.${property}`

export const linearGradientPoints = (width: number, height: number, angleDegrees: unknown) => {
  const angle = finite(angleDegrees, 0) * Math.PI / 180
  const directionX = Math.cos(angle)
  const directionY = Math.sin(angle)
  const centerX = width / 2
  const centerY = height / 2
  const extent = Math.abs(directionX) * width / 2 + Math.abs(directionY) * height / 2
  return {
    start: { x: centerX - directionX * extent, y: centerY - directionY * extent },
    end: { x: centerX + directionX * extent, y: centerY + directionY * extent },
  }
}

export const radialGradientGeometry = (width: number, height: number, gradient: GradientFill) => ({
  center: {
    x: clamp01(gradient.centerX) * width,
    y: clamp01(gradient.centerY) * height,
  },
  radius: Math.max(0.001, clamp01(gradient.radius) * Math.max(width, height)),
})

export type KonvaGradientProps = Record<string, string | number | Array<string | number> | { x: number; y: number }>

export const konvaGradientProps = (
  gradient: GradientFill,
  width: number,
  height: number,
  nodeOrigin = { x: 0, y: 0 },
): KonvaGradientProps => {
  const colorStops = normalizedGradientStops(gradient.stops).flatMap((stop) => [stop.position, stop.color])
  const relativeToNode = (point: { x: number; y: number }) => ({
    x: point.x - nodeOrigin.x,
    y: point.y - nodeOrigin.y,
  })
  if (gradient.type === 'radial') {
    const { center, radius } = radialGradientGeometry(width, height, gradient)
    const nodeCenter = relativeToNode(center)
    return {
      fillPriority: 'radial-gradient',
      fillRadialGradientStartPoint: nodeCenter,
      fillRadialGradientEndPoint: nodeCenter,
      fillRadialGradientStartRadius: 0,
      fillRadialGradientEndRadius: radius,
      fillRadialGradientColorStops: colorStops,
    }
  }
  const { start, end } = linearGradientPoints(width, height, gradient.angle)
  return {
    fillPriority: 'linear-gradient',
    fillLinearGradientStartPoint: relativeToNode(start),
    fillLinearGradientEndPoint: relativeToNode(end),
    fillLinearGradientColorStops: colorStops,
  }
}

export const canvasGradient = (
  context: CanvasRenderingContext2D,
  gradient: GradientFill,
  x: number,
  y: number,
  width: number,
  height: number,
) => {
  let result: CanvasGradient
  if (gradient.type === 'radial') {
    const { center, radius } = radialGradientGeometry(width, height, gradient)
    result = context.createRadialGradient(x + center.x, y + center.y, 0, x + center.x, y + center.y, radius)
  } else {
    const { start, end } = linearGradientPoints(width, height, gradient.angle)
    result = context.createLinearGradient(x + start.x, y + start.y, x + end.x, y + end.y)
  }
  normalizedGradientStops(gradient.stops).forEach((stop) => result.addColorStop(stop.position, stop.color))
  return result
}

const titleCase = (text: string) => text.replace(/[\p{L}\p{M}]+(?:['’][\p{L}\p{M}]+)*/gu, (word) => {
  const characters = Array.from(word.toLocaleLowerCase())
  return characters.length ? characters[0].toLocaleUpperCase() + characters.slice(1).join('') : word
})

export const transformText = (text: unknown, transform: TextTransform) => {
  const value = String(text ?? '')
  if (transform === 'uppercase') return value.toLocaleUpperCase()
  if (transform === 'lowercase') return value.toLocaleLowerCase()
  if (transform === 'titlecase') return titleCase(value)
  return value
}

export const colorWithOpacity = (color: string, opacity: unknown) => {
  const alpha = clamp01(opacity)
  const hex = String(color).trim().match(/^#([\da-f]{3}|[\da-f]{6})$/i)?.[1]
  if (!hex) return color
  const expanded = hex.length === 3 ? Array.from(hex).map((character) => character + character).join('') : hex
  const red = Number.parseInt(expanded.slice(0, 2), 16)
  const green = Number.parseInt(expanded.slice(2, 4), 16)
  const blue = Number.parseInt(expanded.slice(4, 6), 16)
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`
}

export const konvaShadowProps = (shadow: OuterShadow, scale: number) => ({
  shadowEnabled: Boolean(shadow.enabled),
  shadowColor: shadow.color,
  shadowOpacity: clamp01(shadow.opacity),
  shadowOffsetX: finite(shadow.offsetX, 0) * scale,
  shadowOffsetY: finite(shadow.offsetY, 0) * scale,
  shadowBlur: Math.max(0, finite(shadow.blur, 0)) * scale,
})

export const applyCanvasShadow = (context: CanvasRenderingContext2D, shadow: OuterShadow, scale: number) => {
  context.shadowColor = colorWithOpacity(shadow.color, shadow.opacity)
  context.shadowOffsetX = finite(shadow.offsetX, 0) * scale
  context.shadowOffsetY = finite(shadow.offsetY, 0) * scale
  context.shadowBlur = Math.max(0, finite(shadow.blur, 0)) * scale
}
